import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  People,
  CheckCircle,
  AccessTime,
  TrendingUp,
  Close,
  Receipt,
  Business,
  LocalOffer as TariffIcon,
} from '@mui/icons-material';
import billingService, {
  ClientBilling as ClientBillingType,
  ClientsBillingStats,
  Invoice,
} from '../../services/billing.service';
import { CurrencyCell, StatusChip } from '../../components/Common/DataTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ClientBilling: React.FC = () => {
  const { t } = useTranslation();

  const [clients, setClients] = useState<ClientBillingType[]>([]);
  const [stats, setStats] = useState<ClientsBillingStats>({ totalClientes: 0, valorPago: 0, valorPendente: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientBillingType | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceTotal, setInvoiceTotal] = useState(0);

  const INVOICE_ROWS = 5;

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getClientsBilling({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
      });
      if (response.success) {
        setClients(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        if (response.stats) setStats(response.stats);
      }
    } catch (err: any) {
      console.error('Error fetching clients billing:', err);
      setError(t('billing.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchClients(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchClients, search]);

  const fetchClientInvoices = useCallback(async (clientId: string, iPage: number) => {
    setLoadingInvoices(true);
    try {
      const response = await billingService.getInvoices({
        clienteId: clientId,
        page: iPage + 1,
        limit: INVOICE_ROWS,
      });
      if (response.success) {
        setClientInvoices(response.data || []);
        setInvoiceTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching client invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  const handleOpenDialog = (client: ClientBillingType) => {
    setSelectedClient(client);
    setDialogTab(0);
    setInvoicePage(0);
    setDialogOpen(true);
    fetchClientInvoices(client.id, 0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
    setClientInvoices([]);
  };

  const handleInvoicePageChange = (_: unknown, newPage: number) => {
    setInvoicePage(newPage);
    if (selectedClient) fetchClientInvoices(selectedClient.id, newPage);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <People />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalClientes}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('billing.clientsWithInvoices')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(stats.valorPago)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('billing.totalPaid')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                  <AccessTime />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{formatCurrency(stats.valorPendente)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('billing.totalPending')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            size="small"
            placeholder={`${t('common.search')} ${t('billing.clients').toLowerCase()}...`}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Nº {t('billing.invoices')}</TableCell>
                <TableCell align="right">{t('billing.totalPaid')}</TableCell>
                <TableCell align="right">{t('billing.totalPending')}</TableCell>
                <TableCell>{t('billing.clientEntities')}</TableCell>
                <TableCell>{t('billing.clientTariff')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('billing.noClientsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDialog(client)}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{client.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{client.codigo}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{client.email || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={client.totalFaturas} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" fontWeight={500}>
                        {formatCurrency(client.valorPago)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="warning.main" fontWeight={500}>
                        {formatCurrency(client.valorPendente)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                        {client.entidades || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {client.tarifarios ? (
                        <Chip
                          label={client.tarifarios}
                          size="small"
                          icon={<TariffIcon />}
                          variant="outlined"
                          color="secondary"
                          sx={{ maxWidth: 160 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t('common.of')} ${count}`
          }
        />
      </Paper>

      {/* Client Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>{selectedClient?.nome}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClient?.codigo} {selectedClient?.email ? `· ${selectedClient.email}` : ''}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)}>
            <Tab icon={<Receipt />} iconPosition="start" label={t('billing.invoices')} />
            <Tab icon={<Business />} iconPosition="start" label={t('billing.servicesAndTariff')} />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Tab 0 — Faturas */}
          <TabPanel value={dialogTab} index={0}>
            <Box sx={{ p: 2 }}>
              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<Receipt />}
                  label={`${selectedClient?.totalFaturas} ${t('billing.invoices').toLowerCase()}`}
                  size="small"
                />
                <Chip
                  icon={<CheckCircle />}
                  label={`Pago: ${formatCurrency(selectedClient?.valorPago || 0)}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<AccessTime />}
                  label={`Pendente: ${formatCurrency(selectedClient?.valorPendente || 0)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>

              {loadingInvoices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : clientInvoices.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {t('billing.noInvoices')}
                </Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('billing.invoiceNumber')}</TableCell>
                          <TableCell>{t('common.entity')}</TableCell>
                          <TableCell>{t('billing.month')}</TableCell>
                          <TableCell align="right">{t('common.total')}</TableCell>
                          <TableCell align="center">{t('common.status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clientInvoices.map((inv) => (
                          <TableRow key={inv.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500} color="primary">
                                {inv.numero}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{inv.entidade}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{inv.mes} {inv.ano}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <CurrencyCell value={inv.total} />
                            </TableCell>
                            <TableCell align="center">
                              <StatusChip
                                status={inv.estado === 'PAGA' ? t('billing.statusPaid') : t('billing.statusPending')}
                                colorMap={{
                                  [t('billing.statusPaid')]: 'success',
                                  [t('billing.statusPending')]: 'warning',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={invoiceTotal}
                    page={invoicePage}
                    onPageChange={handleInvoicePageChange}
                    rowsPerPage={INVOICE_ROWS}
                    rowsPerPageOptions={[INVOICE_ROWS]}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} ${t('common.of')} ${count}`
                    }
                  />
                </>
              )}
            </Box>
          </TabPanel>

          {/* Tab 1 — Serviços & Tarifário */}
          <TabPanel value={dialogTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Business color="primary" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('billing.clientEntities')}
                        </Typography>
                      </Box>
                      {selectedClient?.entidades ? (
                        selectedClient.entidades.split(', ').map((ent, i) => (
                          <Chip key={i} label={ent} size="small" sx={{ m: 0.5 }} />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrendingUp color="secondary" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('common.service')}s
                        </Typography>
                      </Box>
                      {selectedClient?.servicos ? (
                        selectedClient.servicos.split(', ').map((srv, i) => (
                          <Chip key={i} label={srv} size="small" color="info" variant="outlined" sx={{ m: 0.5 }} />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TariffIcon color="action" fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('billing.clientTariff')}
                        </Typography>
                      </Box>
                      {selectedClient?.tarifarios ? (
                        selectedClient.tarifarios.split(', ').map((tar, i) => (
                          <Chip
                            key={i}
                            label={tar}
                            size="small"
                            color="secondary"
                            icon={<TariffIcon />}
                            sx={{ m: 0.5 }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sem tarifário associado
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientBilling;
